import {naturalSort, noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo, useState} from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD, LEGACY_FONT_PROXIMA_15} from 'styles/FontConstants';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import strUtil from 'util/strUtil';
import {assertNever} from 'util/Types';

import {USER_DEFINED_VIEW_SCHEMA} from '../AnalyticsWarehouseConstants';
import useWarehouseModelsQuery from '../useWarehouseModelsQuery';
import {UserWarehouseModelsData, WarehouseUserDefinedViewMetadataFull} from '../warehouseModelsDataLayout';
import {getTableViewName} from '../WarehouseUtils';
import useTableBrowserScreenState, {
  TableBrowserScreen,
  TableBrowserScreenState,
} from './useTableBrowserScreenState';
import WarehouseTableDetails from './WarehouseTableDetails';
import WarehouseTableListing, {LoadingWarehouseTableListing} from './WarehouseTableListing';

function getMatchedFullTableNames(fullTableNames: Array<string>, searchQuery: string): Array<string> {
  return fullTableNames.filter((fullTableName) =>
    strUtil.caseInsensitiveContains(fullTableName, searchQuery)
  );
}

export interface WarehouseTableBrowserState {
  onOpenListing: () => void;
  onOpenTable: (fullTableName: string) => void;
  screenState: TableBrowserScreenState;
  searchQuery: string;
  setSearchQuery: (newSearchQuery: string) => void;
}

/**
 * This hook is used by parents of this component to keep track of the table browser's state in between
 * render updates.
 */
export function useWarehouseTableBrowserState(): WarehouseTableBrowserState {
  const screenState = useTableBrowserScreenState();
  const [searchQuery, setSearchQuery] = useState<string>('');

  // useMemo is useful to avoid updates to the React.memo below
  return useMemo(
    () => ({...screenState, searchQuery, setSearchQuery}),
    [screenState, searchQuery, setSearchQuery]
  );
}

export interface WarehouseTableBrowserProps {
  additionalActionButtons?: JSX.Element;
  warehouseModelsData: UserWarehouseModelsData;
  warehouseBrowserState: WarehouseTableBrowserState;
}

// The stubbable functions have to be defined as part of some other object to be stubbable
export const _stubbableWarehouseTableFunctions = {
  _stubbableWarehouseTableBrowserRender: noop,
};

export function extractUserDefinedViewsData(
  warehouseModelsData: UserWarehouseModelsData
): Array<WarehouseUserDefinedViewMetadataFull> {
  const viewsModelData = warehouseModelsData.get(USER_DEFINED_VIEW_SCHEMA);
  if (!viewsModelData) {
    return [];
  }

  return Array.from(viewsModelData.tables.values()).map((table) => ({
    id: table.warehouseUserDefinedViewMetadata!.id,
    name: table.name,
    description: table.description!,
    viewQuery: table.warehouseUserDefinedViewMetadata!.viewQuery,
    existsInWarehouse: table.warehouseUserDefinedViewMetadata!.existsInWarehouse,
    permissions: table.warehouseUserDefinedViewMetadata!.permissions,
  }));
}
export function WarehouseTableBrowser({
  additionalActionButtons,
  warehouseModelsData,
  warehouseBrowserState,
}: WarehouseTableBrowserProps): JSX.Element | null {
  const {onOpenListing, onOpenTable, screenState, searchQuery, setSearchQuery} = warehouseBrowserState;

  // Extract user-defined views
  const userDefinedViewsData = useMemo<Array<WarehouseUserDefinedViewMetadataFull>>(
    () => extractUserDefinedViewsData(warehouseModelsData),
    [warehouseModelsData]
  );

  const sortedFullTableNames = useMemo(
    () =>
      naturalSort(
        [...warehouseModelsData.entries()]
          .flatMap(([schemaName, modelsData]) =>
            [...modelsData.tables.keys()].map((completeTableName) =>
              _.unique([
                // We remove duplicates here because the "view name" of a table may be the same
                // as the complete table name.
                // TODO(BNCH-29085): Abstract to getFullTableName function in shared helper file
                `${schemaName}.${completeTableName}`,
                // TODO(BNCH-29085): Abstract to getFullTableViewName function in shared helper file
                getTableViewName(schemaName, completeTableName),
              ])
            )
          )
          .flat()
      ),
    [warehouseModelsData]
  );
  const matchedSortedFullTableNames = getMatchedFullTableNames(sortedFullTableNames, searchQuery);

  // We call a noop function in the render function of the component to be able to stub it. We can't directly
  // stub the functional component because it's memo'd, so instead we can stub a function that is called on
  // render. Using this noop gives us a consistent function to do that with. This is useful to test if a
  // render happened or not with certain actions (to make sure we're not breaking the React.memo)
  //
  // See "does not frivolously re-render the table browser when the query is updated" in
  // client/insights/analytics-queries/block-editor/__tests__/AnalyticsBlockEditorContent-test.tsx for usage.
  _stubbableWarehouseTableFunctions._stubbableWarehouseTableBrowserRender();

  switch (screenState.screen) {
    case TableBrowserScreen.LISTING:
      return (
        <WarehouseTableListing
          actions={{openTable: onOpenTable, setQuery: setSearchQuery}}
          additionalActionButtons={additionalActionButtons}
          matchedSortedFullTableNames={matchedSortedFullTableNames}
          query={searchQuery}
          userDefinedViewsData={userDefinedViewsData}
        />
      );
    case TableBrowserScreen.DETAILS: {
      const userDefinedViewForTable = userDefinedViewsData.find(
        (view) => `${USER_DEFINED_VIEW_SCHEMA}.${view.name}` === screenState.fullTableName
      );
      return (
        <WarehouseTableDetails
          additionalActionButtons={additionalActionButtons}
          fullTableName={screenState.fullTableName}
          onOpenListing={onOpenListing}
          userDefinedViewData={userDefinedViewForTable}
        />
      );
    }
    default:
      assertAndContinue(false, `Unexpected screen: ${assertNever(screenState)}.`);
      return null;
  }
}

function WarehouseTableBrowserError(): JSX.Element {
  return (
    <div className={classNames(css(styles.errorWrapper), 'u-text-align-center')}>
      <BenchlingIcon icon="warning" className={classNames(css(styles.errorIcon))} />
      <div className={css(styles.errorText)}>
        Schema browser did not load correctly. Please refresh to try again.
      </div>
    </div>
  );
}

function QueryingWarehouseTableBrowser(
  props: Omit<WarehouseTableBrowserProps, 'warehouseModelsData'>
): JSX.Element {
  const result = useWarehouseModelsQuery();
  switch (result.queryResultType) {
    case QueryResultType.SUCCESS:
      return <WarehouseTableBrowser {...props} warehouseModelsData={result.transformedData} />;
    case QueryResultType.PENDING:
      return <LoadingWarehouseTableListing />;
    default:
      return <WarehouseTableBrowserError />;
  }
}

export default React.memo(QueryingWarehouseTableBrowser);

const styles = StyleSheet.create({
  errorWrapper: {
    marginTop: 16,
  },
  errorIcon: {
    color: GRAY_LIGHT,
    height: 60,
    width: 60,
  },
  errorText: {
    ...LEGACY_FONT_PROXIMA_15,
    color: GRAY_DARK,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
